import React, { useState, useEffect } from 'react';
import { supabase } from '../services/supabase';
import { FiPlus, FiTrash2, FiPrinter, FiEdit, FiX, FiCheck, FiSave, FiHome, FiFileText, FiRefreshCw } from 'react-icons/fi';
import styles from './AdminAddressGenerator.module.css';

const DEFAULT_FROM = {
  name: 'Pavan Kalyan',
  address1: '5-93, Verakalaveedhi',
  address2: 'Etikoppaka Village, Elamanchili (M)',
  city: 'Anakapalle(D)',
  state: 'Andhra Pradesh',
  pincode: '531082',
  phone: '9014884214',
  email: 'info@etikoppakatoys.com'
};

const AdminAddressGenerator = () => {
  const [fromAddress, setFromAddress] = useState(DEFAULT_FROM);
  const [toAddresses, setToAddresses] = useState([]);       // manually added
  const [orderAddresses, setOrderAddresses] = useState([]); // from orders
  const [selectedIds, setSelectedIds] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [isEditingFrom, setIsEditingFrom] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    pincode: '',
    phone: '',
    email: ''
  });
  const [toast, setToast] = useState(null);
  const [showPrintOptions, setShowPrintOptions] = useState(false);
  const [loadingOrders, setLoadingOrders] = useState(false);

  // ── Load saved addresses from localStorage ──
  useEffect(() => {
    const savedFrom = localStorage.getItem('addressGenerator_from');
    if (savedFrom) try { setFromAddress(JSON.parse(savedFrom)); } catch {}
    const savedTo = localStorage.getItem('addressGenerator_to');
    if (savedTo) try { setToAddresses(JSON.parse(savedTo)); } catch {}
    // Fetch order addresses from Supabase
    fetchOrderAddresses();
  }, []);

  // ── Save manual addresses to localStorage ──
  useEffect(() => {
    localStorage.setItem('addressGenerator_from', JSON.stringify(fromAddress));
  }, [fromAddress]);
  useEffect(() => {
    localStorage.setItem('addressGenerator_to', JSON.stringify(toAddresses));
  }, [toAddresses]);

  // ── Toast ──
  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  // ── FROM handlers ──
  const handleFromChange = (e) => {
    setFromAddress({ ...fromAddress, [e.target.name]: e.target.value });
  };

  // ── TO (manual) handlers ──
  const handleToChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const resetToForm = () => {
    setFormData({
      name: '',
      address1: '',
      address2: '',
      city: '',
      state: '',
      pincode: '',
      phone: '',
      email: ''
    });
    setEditingId(null);
  };

  const handleToSubmit = (e) => {
    e.preventDefault();
    if (!formData.name || !formData.address1 || !formData.city || !formData.pincode) {
      showToast('Please fill all required fields (Name, Address1, City, Pincode)', 'error');
      return;
    }

    if (editingId) {
      setToAddresses(toAddresses.map(addr =>
        addr.id === editingId ? { ...addr, ...formData, source: 'manual' } : addr
      ));
      showToast('Address updated!', 'success');
    } else {
      const newAddress = { id: Date.now().toString(), ...formData, source: 'manual' };
      setToAddresses([...toAddresses, newAddress]);
      showToast('Address added!', 'success');
    }
    resetToForm();
  };

  const handleEditTo = (id) => {
    const addr = toAddresses.find(a => a.id === id);
    if (addr) {
      setFormData(addr);
      setEditingId(id);
    }
  };

  const handleDeleteTo = (id) => {
    // Only allow deleting manual addresses
    const addr = toAddresses.find(a => a.id === id);
    if (addr?.source === 'order') {
      showToast('Order addresses cannot be deleted manually.', 'warning');
      return;
    }
    if (!window.confirm('Delete this address?')) return;
    setToAddresses(toAddresses.filter(a => a.id !== id));
    setSelectedIds(selectedIds.filter(sid => sid !== id));
    if (editingId === id) resetToForm();
    showToast('Address deleted', 'warning');
  };

  const handleSelectTo = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(sid => sid !== id) : [...prev, id]
    );
  };

  const selectAllTo = () => {
    const allIds = [...toAddresses, ...orderAddresses].map(a => a.id);
    setSelectedIds(
      selectedIds.length === allIds.length
        ? []
        : allIds
    );
  };

  const handleDeleteSelected = () => {
    // Only delete manual addresses
    const toDelete = selectedIds.filter(id => toAddresses.some(a => a.id === id));
    if (toDelete.length === 0) {
      showToast('No manual addresses selected to delete.', 'warning');
      return;
    }
    if (!window.confirm(`Delete ${toDelete.length} selected address(es)?`)) return;
    setToAddresses(toAddresses.filter(a => !toDelete.includes(a.id)));
    setSelectedIds(selectedIds.filter(sid => !toDelete.includes(sid)));
    showToast('Selected addresses deleted', 'warning');
  };

  // ── Fetch order addresses from Supabase ──
  const fetchOrderAddresses = async () => {
  setLoadingOrders(true);
  try {
    // 1. Fetch orders with shipping_address and user_id
    const { data: orders, error: ordersError } = await supabase
      .from('orders')
      .select('id, user_id, shipping_address')
      .order('created_at', { ascending: false });

    if (ordersError) throw ordersError;

    if (!orders || orders.length === 0) {
      setOrderAddresses([]);
      setLoadingOrders(false);
      return;
    }

    // 2. Collect unique user_ids
    const userIds = [...new Set(orders.map(o => o.user_id).filter(Boolean))];

    // 3. Fetch user details for those IDs
    let usersMap = {};
    if (userIds.length > 0) {
      const { data: users, error: usersError } = await supabase
        .from('users')
        .select('id, name, email, mobile')
        .in('id', userIds);

      if (usersError) throw usersError;

      usersMap = users.reduce((acc, user) => {
        acc[user.id] = user;
        return acc;
      }, {});
    }

    // 4. Parse and combine
    const parsed = orders.map(order => {
      const addr = order.shipping_address || '';
      const lines = addr.split(/[\n,]/).map(s => s.trim()).filter(Boolean);
      const user = usersMap[order.user_id] || {};

      const name = user.name || lines[0] || 'Customer';
      const address1 = lines[1] || '';
      const address2 = lines[2] || '';
      const lastLine = lines[lines.length - 1] || '';
      const pincodeMatch = lastLine.match(/\b\d{6}\b/);
      const pincode = pincodeMatch ? pincodeMatch[0] : '';
      const cityState = lastLine.replace(/\b\d{6}\b/, '').replace(/[,-]/g, '').trim();
      const city = cityState;
      const state = '';
      const phone = user.mobile || '';
      const email = user.email || '';

      return {
        id: `order-${order.id}`,
        name,
        address1,
        address2,
        city,
        state,
        pincode,
        phone,
        email,
        source: 'order'
      };
    });

    setOrderAddresses(parsed);
  } catch (err) {
    console.error('Error fetching order addresses:', err);
    showToast('Failed to load order addresses', 'error');
  } finally {
    setLoadingOrders(false);
  }
};

  // ── Print labels (unchanged logic, now includes both sources) ──
  const generateLabelsPdf = () => {
    const allAddresses = [...toAddresses, ...orderAddresses];
    const toPrint = selectedIds.length
      ? allAddresses.filter(a => selectedIds.includes(a.id))
      : allAddresses;

    if (!toPrint.length) {
      showToast('No addresses selected to print', 'error');
      return;
    }

    const printWindow = window.open('', '_blank', 'width=800,height=600');
    if (!printWindow) {
      alert('Please allow pop‑ups for this site');
      return;
    }

    const labelsHtml = toPrint.map(addr => `
      <div class="label">
        <div class="label-to">
          <div class="label-to-title">To:</div>
          <div><strong>${addr.name}</strong></div>
          <div>${addr.address1}</div>
          ${addr.address2 ? `<div>${addr.address2}</div>` : ''}
          <div>${addr.city}${addr.state ? `, ${addr.state}` : ''} - ${addr.pincode}</div>
          ${addr.phone ? `<div>📞 ${addr.phone}</div>` : ''}
          ${addr.email ? `<div>✉ ${addr.email}</div>` : ''}
        </div>
        <div class="label-divider"></div>
        <div class="label-from">
          <div class="label-from-title">From:</div>
          <div><strong>${fromAddress.name}</strong></div>
          <div>${fromAddress.address1}</div>
          ${fromAddress.address2 ? `<div>${fromAddress.address2}</div>` : ''}
          <div>${fromAddress.city}${fromAddress.state ? `, ${fromAddress.state}` : ''} - ${fromAddress.pincode}</div>
          <div>📞 ${fromAddress.phone}</div>
          ${fromAddress.email ? `<div>✉ ${fromAddress.email}</div>` : ''}
        </div>
      </div>
    `).join('');

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Shipping Labels</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { background: #f0f0f0; padding: 20px; font-family: Arial, Helvetica, sans-serif; }
          .label {
            display: inline-block; width: 300px; min-height: 200px; background: white;
            border: 1px solid #ccc; border-radius: 8px; padding: 12px; margin: 12px;
            box-shadow: 0 2px 6px rgba(0,0,0,0.1); vertical-align: top; page-break-inside: avoid;
            font-size: 12px; line-height: 1.5;
          }
          .label-to {
            border-bottom: 2px solid #9E1B1B;
            padding-bottom: 8px;
            margin-bottom: 8px;
            color: #1a1a1a;
          }
          .label-to strong { font-size: 14px; }
          .label-from {
            color: #333;
          }
          .label-from strong { font-size: 13px; }
          .label-to-title, .label-from-title {
            font-weight: bold;
            font-size: 11px;
            letter-spacing: 0.5px;
            margin-bottom: 4px;
          }
          .label-to-title { color: #9E1B1B; }
          .label-from-title { color: #1F5B3A; }
          .label-divider {
            height: 2px;
            background: #e0dcd6;
            margin: 6px 0;
          }
          @media print {
            body { background: white; padding: 0; }
            .label { box-shadow: none; border-color: #aaa; }
          }
        </style>
      </head>
      <body>
        <div class="labels-container">${labelsHtml}</div>
      </body>
      </html>
    `;

    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const generateLabelsPrinter = () => {
    const allAddresses = [...toAddresses, ...orderAddresses];
    const toPrint = selectedIds.length
      ? allAddresses.filter(a => selectedIds.includes(a.id))
      : allAddresses;

    if (!toPrint.length) {
      showToast('No addresses selected to print', 'error');
      return;
    }

    const printWindow = window.open('', '_blank', 'width=800,height=600');
    if (!printWindow) {
      alert('Please allow pop‑ups for this site');
      return;
    }

    const labelsHtml = toPrint.map(addr => `
      <div class="label">
        <div class="label-to">
          <div class="label-to-title">To:</div>
          <div><strong>${addr.name}</strong></div>
          <div>${addr.address1}</div>
          ${addr.address2 ? `<div>${addr.address2}</div>` : ''}
          <div>${addr.city}${addr.state ? `, ${addr.state}` : ''} - ${addr.pincode}</div>
          ${addr.phone ? `<div>📞 ${addr.phone}</div>` : ''}
          ${addr.email ? `<div>✉ ${addr.email}</div>` : ''}
        </div>
        <div class="label-divider"></div>
        <div class="label-from">
          <div class="label-from-title">From:</div>
          <div><strong>${fromAddress.name}</strong></div>
          <div>${fromAddress.address1}</div>
          ${fromAddress.address2 ? `<div>${fromAddress.address2}</div>` : ''}
          <div>${fromAddress.city}${fromAddress.state ? `, ${fromAddress.state}` : ''} - ${fromAddress.pincode}</div>
          <div>📞 ${fromAddress.phone}</div>
          ${fromAddress.email ? `<div>✉ ${fromAddress.email}</div>` : ''}
        </div>
      </div>
    `).join('');

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Shipping Labels (Printer)</title>
        <style>
          @page { size: 4in 6in; margin: 0.1in; }
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { background: white; padding: 0; margin: 0; font-family: Arial, Helvetica, sans-serif; }
          .label {
            width: 4in;
            height: 6in;
            background: white;
            border: 1px solid #ccc;
            border-radius: 4px;
            padding: 0.25in;
            margin: 0 auto;
            page-break-after: always;
            display: flex;
            flex-direction: column;
            justify-content: center;
            font-size: 11pt;
            line-height: 1.5;
            box-sizing: border-box;
          }
          .label-to {
            border-bottom: 2px solid #9E1B1B;
            padding-bottom: 6px;
            margin-bottom: 6px;
          }
          .label-to strong { font-size: 14pt; }
          .label-from strong { font-size: 13pt; }
          .label-to-title, .label-from-title {
            font-weight: bold;
            font-size: 10pt;
            letter-spacing: 0.5px;
            margin-bottom: 2px;
          }
          .label-to-title { color: #9E1B1B; }
          .label-from-title { color: #1F5B3A; }
          .label-divider { height: 2px; background: #e0dcd6; margin: 6px 0; }
          .label-phone, .label-email { font-size: 10pt; color: #555; margin-top: 3px; }
          .label-from { color: #333; }
          @media print {
            .label { border: none; }
          }
        </style>
      </head>
      <body>
        <div class="labels-container">${labelsHtml}</div>
      </body>
      </html>
    `;

    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const handlePrintClick = () => {
    const allAddresses = [...toAddresses, ...orderAddresses];
    if (allAddresses.length === 0) {
      showToast('No addresses to print. Add manual addresses or sync orders.', 'error');
      return;
    }
    setShowPrintOptions(true);
  };

  // ── Render ──
  const allAddresses = [...toAddresses, ...orderAddresses];

  return (
    <div className={styles.addressGenerator}>
      {toast && (
        <div className={`${styles.toast} ${styles[toast.type]}`}>
          {toast.message}
        </div>
      )}

      <div className={styles.header}>
        <h2>Address Label Generator</h2>
        <p>Manage FROM (your business) and TO (customer) addresses</p>
      </div>

      <div className={styles.mainLayout}>
        {/* ── FROM Section ── */}
        <div className={styles.fromSection}>
          <div className={styles.sectionHeader}>
            <h3><FiHome /> FROM Address (Your Business)</h3>
            <button onClick={() => setIsEditingFrom(!isEditingFrom)} className={styles.editFromBtn}>
              {isEditingFrom ? <FiX /> : <FiEdit />} {isEditingFrom ? 'Cancel' : 'Edit'}
            </button>
          </div>
          {isEditingFrom ? (
            <form
              className={styles.fromForm}
              onSubmit={(e) => {
                e.preventDefault();
                setIsEditingFrom(false);
                showToast('FROM address updated!', 'success');
              }}
            >
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>Name</label>
                  <input name="name" value={fromAddress.name} onChange={handleFromChange} />
                </div>
                <div className={styles.formGroup}>
                  <label>Phone</label>
                  <input name="phone" value={fromAddress.phone} onChange={handleFromChange} />
                </div>
              </div>
              <div className={styles.formGroup}>
                <label>Address 1</label>
                <input name="address1" value={fromAddress.address1} onChange={handleFromChange} />
              </div>
              <div className={styles.formGroup}>
                <label>Address 2</label>
                <input name="address2" value={fromAddress.address2} onChange={handleFromChange} />
              </div>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>City</label>
                  <input name="city" value={fromAddress.city} onChange={handleFromChange} />
                </div>
                <div className={styles.formGroup}>
                  <label>State (optional)</label>
                  <input name="state" value={fromAddress.state} onChange={handleFromChange} />
                </div>
                <div className={styles.formGroup}>
                  <label>Pincode</label>
                  <input name="pincode" value={fromAddress.pincode} onChange={handleFromChange} />
                </div>
              </div>
              <div className={styles.formGroup}>
                <label>Email</label>
                <input name="email" value={fromAddress.email} onChange={handleFromChange} />
              </div>
              <div className={styles.formActions}>
                <button type="submit" className={styles.saveBtn}><FiSave /> Save</button>
              </div>
            </form>
          ) : (
            <div className={styles.fromDisplay}>
              <div><strong>{fromAddress.name}</strong></div>
              <div>{fromAddress.address1}</div>
              {fromAddress.address2 && <div>{fromAddress.address2}</div>}
              <div>
                {fromAddress.city}
                {fromAddress.state ? `, ${fromAddress.state}` : ''} - {fromAddress.pincode}
              </div>
              <div>📞 {fromAddress.phone}</div>
              {fromAddress.email && <div>✉ {fromAddress.email}</div>}
            </div>
          )}
        </div>

        {/* ── TO Section ── */}
        <div className={styles.toSection}>
          <div className={styles.sectionHeader}>
            <h3>TO Addresses</h3>
            <div className={styles.sectionActions}>
              <button onClick={selectAllTo} className={styles.selectAllBtn}>
                {selectedIds.length === allAddresses.length ? 'Deselect All' : 'Select All'}
              </button>
              <button onClick={fetchOrderAddresses} className={styles.syncBtn}>
                <FiRefreshCw /> Sync Orders
              </button>
              {selectedIds.length > 0 && (
                <>
                  <button onClick={handlePrintClick} className={styles.printSelectedBtn}>
                    <FiPrinter /> Print ({selectedIds.length})
                  </button>
                  <button onClick={handleDeleteSelected} className={styles.deleteSelectedBtn}>
                    <FiTrash2 /> Delete Selected
                  </button>
                </>
              )}
            </div>
          </div>

          {/* ── Add Manual Address ── */}
          <form onSubmit={handleToSubmit} className={styles.toForm}>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label>Name *</label>
                <input
                  name="name"
                  value={formData.name}
                  onChange={handleToChange}
                  placeholder="Full name"
                  required
                />
              </div>
              <div className={styles.formGroup}>
                <label>Phone</label>
                <input
                  name="phone"
                  value={formData.phone}
                  onChange={handleToChange}
                  placeholder="Phone"
                />
              </div>
            </div>
            <div className={styles.formGroup}>
              <label>Address 1 *</label>
              <input
                name="address1"
                value={formData.address1}
                onChange={handleToChange}
                placeholder="Street address"
                required
              />
            </div>
            <div className={styles.formGroup}>
              <label>Address 2</label>
              <input
                name="address2"
                value={formData.address2}
                onChange={handleToChange}
                placeholder="Apartment, suite, etc."
              />
            </div>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label>City *</label>
                <input
                  name="city"
                  value={formData.city}
                  onChange={handleToChange}
                  placeholder="City"
                  required
                />
              </div>
              <div className={styles.formGroup}>
                <label>State (optional)</label>
                <input
                  name="state"
                  value={formData.state}
                  onChange={handleToChange}
                  placeholder="State (optional)"
                />
              </div>
              <div className={styles.formGroup}>
                <label>Pincode *</label>
                <input
                  name="pincode"
                  value={formData.pincode}
                  onChange={handleToChange}
                  placeholder="Pincode"
                  required
                />
              </div>
            </div>
            <div className={styles.formGroup}>
              <label>Email</label>
              <input
                name="email"
                value={formData.email}
                onChange={handleToChange}
                placeholder="Email"
              />
            </div>
            <div className={styles.formActions}>
              <button type="submit" className={styles.submitBtn}>
                {editingId ? <><FiCheck /> Update</> : <><FiPlus /> Add Address</>}
              </button>
              {editingId && (
                <button type="button" onClick={resetToForm} className={styles.cancelBtn}>
                  <FiX /> Cancel
                </button>
              )}
            </div>
          </form>

          {/* ── Address List ── */}
          {allAddresses.length === 0 ? (
            <div className={styles.emptyState}>
              <p>No addresses. Add manual addresses or click "Sync Orders" to load from orders.</p>
            </div>
          ) : (
            <div className={styles.addressGrid}>
              {allAddresses.map(addr => (
                <div key={addr.id} className={`${styles.addressCard} ${addr.source === 'order' ? styles.orderCard : ''}`}>
                  <div className={styles.cardHeader}>
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(addr.id)}
                      onChange={() => handleSelectTo(addr.id)}
                    />
                    {addr.source === 'order' && (
                      <span className={styles.orderBadge}>📦 Order</span>
                    )}
                    <div className={styles.cardActions}>
                      {addr.source !== 'order' && (
                        <button onClick={() => handleEditTo(addr.id)} className={styles.editBtn}>
                          <FiEdit />
                        </button>
                      )}
                      {addr.source !== 'order' && (
                        <button onClick={() => handleDeleteTo(addr.id)} className={styles.deleteBtn}>
                          <FiTrash2 />
                        </button>
                      )}
                    </div>
                  </div>
                  <div className={styles.cardBody}>
                    <div className={styles.cardName}>{addr.name}</div>
                    <div>{addr.address1}</div>
                    {addr.address2 && <div>{addr.address2}</div>}
                    <div>
                      {addr.city}
                      {addr.state ? `, ${addr.state}` : ''} - {addr.pincode}
                    </div>
                    {addr.phone && <div className={styles.cardPhone}>📞 {addr.phone}</div>}
                    {addr.email && <div className={styles.cardEmail}>✉ {addr.email}</div>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Bottom Bar ── */}
      <div className={styles.bottomBar}>
        <button onClick={handlePrintClick} className={styles.generateBtn}>
          <FiPrinter /> Generate & Print Labels
        </button>
        <span className={styles.labelCount}>
          {allAddresses.length} addresses • {selectedIds.length} selected
        </span>
      </div>

      {/* ── Print Options Modal ── */}
      {showPrintOptions && (
        <div className={styles.modalOverlay} onClick={() => setShowPrintOptions(false)}>
          <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Print Options</h3>
              <button onClick={() => setShowPrintOptions(false)} className={styles.modalCloseBtn}>✕</button>
            </div>
            <div className={styles.modalBody}>
              <p>Choose how you want to generate your labels:</p>
              <div className={styles.modalButtons}>
                <button
                  onClick={() => {
                    setShowPrintOptions(false);
                    generateLabelsPdf();
                  }}
                  className={styles.pdfBtn}
                >
                  <FiFileText /> PDF (A4)
                  <span className={styles.btnSubText}>2 columns • Professional layout</span>
                </button>
                <button
                  onClick={() => {
                    setShowPrintOptions(false);
                    generateLabelsPrinter();
                  }}
                  className={styles.labelBtn}
                >
                  <FiPrinter /> Label Printer (4×6)
                  <span className={styles.btnSubText}>One per page • Thermal ready</span>
                </button>
              </div>
              <button
                onClick={() => setShowPrintOptions(false)}
                className={styles.modalCancelBtn}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAddressGenerator;